import React, { useEffect, useState } from 'react';
import {
  getGames, createGame, updateGame, deleteGame,
  getGameActions, createGameAction, updateGameAction, deleteGameAction,
  Game, GameAction,
} from './api';

const KEY_LABELS: Record<string, string> = {
  space: 'Espace', lshift: 'Shift', lctrl: 'Ctrl', lalt: 'Alt',
  mouse1: 'Clic gauche', mouse2: 'Clic droit', mouse3: 'Clic milieu',
  enter: 'Entrée', backspace: 'Retour', tab: 'Tab', escape: 'Échap',
  up: '↑', down: '↓', left: '←', right: '→',
};

function formatKey(val: string): string {
  return val.split('+').map(k => KEY_LABELS[k.toLowerCase()] ?? k.toUpperCase()).join(' + ');
}

interface ActionFormState { name: string; description: string; action_type: string; action_value: string; duration_ms: number; }
const emptyAction = (): ActionFormState => ({ name: '', description: '', action_type: 'key', action_value: '', duration_ms: 100 });
interface GameFormState { name: string; slug: string; image_url: string; platforms: string }
const emptyGame = (): GameFormState => ({ name: '', slug: '', image_url: '', platforms: 'pc' });

function slugify(s: string) { return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''); }

interface Props { isAdmin: boolean; isMobile?: boolean; headerSlot?: React.ReactNode; }

export default function GameLibrary({ isAdmin, isMobile, headerSlot }: Props) {
  const [games, setGames] = useState<Game[]>([]);
  const [selected, setSelected] = useState<Game | null>(null);
  const [search, setSearch] = useState('');
  const [actions, setActions] = useState<GameAction[]>([]);
  const [loadingActions, setLoadingActions] = useState(false);

  const [showGameForm, setShowGameForm] = useState(false);
  const [editingGame, setEditingGame] = useState<Game | null>(null);
  const [gameForm, setGameForm] = useState<GameFormState>(emptyGame());

  const [editingAction, setEditingAction] = useState<GameAction | null>(null);
  const [showActionForm, setShowActionForm] = useState(false);
  const [actionForm, setActionForm] = useState<ActionFormState>(emptyAction());

  const loadGames = async () => { const res = await getGames(); setGames(res.data); };
  const loadActions = async (gameId: number) => {
    setLoadingActions(true);
    const res = await getGameActions(gameId);
    setActions(res.data); setLoadingActions(false);
  };

  useEffect(() => { loadGames(); }, []);
  useEffect(() => { if (selected) loadActions(selected.id); else setActions([]); }, [selected]);

  const openAddGame = () => { setEditingGame(null); setGameForm(emptyGame()); setShowGameForm(true); };
  const openEditGame = (g: Game, e: React.MouseEvent) => {
    e.stopPropagation(); setEditingGame(g); setGameForm({ name: g.name, slug: g.slug, image_url: g.image_url ?? '', platforms: g.platforms || 'pc' }); setShowGameForm(true);
  };
  const togglePlatform = (p: string) => setGameForm(f => {
    const set = new Set((f.platforms || '').split(',').filter(Boolean));
    if (set.has(p)) set.delete(p); else set.add(p);
    if (set.size === 0) set.add('pc');
    return { ...f, platforms: ['pc', 'ps', 'xbox'].filter(x => set.has(x)).join(',') };
  });
  const saveGame = async () => {
    if (!gameForm.name.trim()) return;
    const slug = gameForm.slug || slugify(gameForm.name);
    const image_url = gameForm.image_url || null;
    const platforms = gameForm.platforms || 'pc';
    if (editingGame) await updateGame(editingGame.id, gameForm.name, image_url, platforms);
    else await createGame(gameForm.name, slug, image_url, platforms);
    setShowGameForm(false); loadGames();
  };
  const handleDeleteGame = async (g: Game, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer "${g.name}" et toutes ses actions ?`)) return;
    await deleteGame(g.id);
    if (selected?.id === g.id) setSelected(null);
    loadGames();
  };

  const openAddAction = () => { setEditingAction(null); setActionForm(emptyAction()); setShowActionForm(true); };
  const openEditAction = (a: GameAction) => {
    setEditingAction(a);
    setActionForm({ name: a.name, description: a.description, action_type: a.action_type, action_value: a.action_value, duration_ms: a.duration_ms });
    setShowActionForm(true);
  };
  const saveAction = async () => {
    if (!selected || !actionForm.name.trim() || !actionForm.action_value.trim()) return;
    if (editingAction) await updateGameAction(editingAction.id, actionForm);
    else await createGameAction(selected.id, actionForm);
    setShowActionForm(false); loadActions(selected.id);
  };
  const handleDeleteAction = async (a: GameAction) => {
    if (!window.confirm(`Supprimer l'action "${a.name}" ?`)) return;
    await deleteGameAction(a.id);
    if (selected) loadActions(selected.id);
  };

  const filteredGames = games.filter(g => g.name.toLowerCase().includes(search.toLowerCase()));

  // ── Mobile page navigation ───────────────────────────────
  if (isMobile) {
    // Si jeu sélectionné → page dédiée
    if (selected) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          {/* Header back */}
          <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button onClick={() => setSelected(null)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: '#64748b', fontSize: 12, cursor: 'pointer', padding: '4px 8px', borderRadius: 6 }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              Jeux
            </button>
            <div style={{ width: 1, height: 16, background: '#e2e8f0' }} />
            {selected.image_url && (
              <img src={selected.image_url} alt={selected.name} style={{ width: 28, height: 42, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            )}
            <div style={{ color: '#0f172a', fontWeight: 700, fontSize: 15, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.name}</div>
            {isAdmin && <button onClick={openAddAction} style={btnPrimary}>+ Action</button>}
          </div>
          {/* Actions list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', background: '#f8fafc' }}>
            {loadingActions ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', paddingTop: 40 }}>Chargement…</div>
            ) : actions.length === 0 ? (
              <div style={{ textAlign: 'center', paddingTop: 40, color: '#94a3b8', fontSize: 13 }}>Aucune action.{isAdmin ? ' Clique sur "+ Action".' : ''}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {actions.map(a => (
                  <div key={a.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}>
                      {a.action_type === 'gamepad' ? '🎮' : '⌨️'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 13 }}>{a.name}</div>
                      {a.description && <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 1 }}>{a.description}</div>}
                      <span style={{ display: 'inline-block', marginTop: 4, padding: '2px 8px', borderRadius: 5, background: '#f1f5f9', color: '#374151', fontSize: 11, fontWeight: 600 }}>{formatKey(a.action_value)}</span>
                    </div>
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => openEditAction(a)} style={iconBtn}>✏️</button>
                        <button onClick={() => handleDeleteAction(a)} style={{ ...iconBtn, color: '#dc2626' }}>🗑</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Modals */}
          {showGameForm && (
            <Modal title={editingGame ? 'Modifier le jeu' : 'Ajouter un jeu'} onClose={() => setShowGameForm(false)}>
              <LightField label="Nom du jeu">
                <input style={inputLight} value={gameForm.name} placeholder="ex: Minecraft"
                  onChange={e => { const name = e.target.value; setGameForm(f => ({ ...f, name, slug: editingGame ? f.slug : slugify(name) })); }} />
              </LightField>
              {!editingGame && (
                <LightField label="Identifiant unique (slug)">
                  <input style={inputLight} value={gameForm.slug} placeholder="ex: minecraft"
                    onChange={e => setGameForm(f => ({ ...f, slug: e.target.value }))} />
                </LightField>
              )}
              <LightField label="URL image couverture (portrait 2:3)">
                <input style={inputLight} value={gameForm.image_url} placeholder="https://…"
                  onChange={e => setGameForm(f => ({ ...f, image_url: e.target.value }))} />
              </LightField>
              {gameForm.image_url && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <img src={gameForm.image_url} alt="preview" style={{ width: 70, height: 105, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }}
                    onError={e => (e.target as HTMLImageElement).style.display = 'none'} />
                </div>
              )}
              <LightField label="Plateformes (pour les actions manette / Remote Play)">
                <div style={{ display: 'flex', gap: 8 }}>
                  {([['pc', '🖥️ PC'], ['ps', '🎮 PlayStation'], ['xbox', '🟢 Xbox']] as const).map(([k, label]) => {
                    const on = (gameForm.platforms || '').split(',').includes(k);
                    return (
                      <button key={k} type="button" onClick={() => togglePlatform(k)}
                        style={{ flex: 1, padding: '8px 6px', borderRadius: 8, border: '1px solid', borderColor: on ? '#7c3aed' : '#e2e8f0', background: on ? '#f5f3ff' : '#fff', color: on ? '#7c3aed' : '#64748b', fontSize: 12, fontWeight: on ? 800 : 600, cursor: 'pointer' }}>
                        {on ? '✓ ' : ''}{label}
                      </button>
                    );
                  })}
                </div>
              </LightField>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                <button style={btnGhost} onClick={() => setShowGameForm(false)}>Annuler</button>
                <button style={btnPrimary} onClick={saveGame}>{editingGame ? 'Enregistrer' : 'Ajouter'}</button>
              </div>
            </Modal>
          )}
          {showActionForm && (
            <Modal title={editingAction ? "Modifier l'action" : 'Nouvelle action'} onClose={() => setShowActionForm(false)}>
              <LightField label="Nom de l'action">
                <input style={inputLight} value={actionForm.name} placeholder="ex: Sauter"
                  onChange={e => setActionForm(f => ({ ...f, name: e.target.value }))} />
              </LightField>
              <LightField label="Description (optionnel)">
                <input style={inputLight} value={actionForm.description} placeholder="ex: Fait sauter le personnage"
                  onChange={e => setActionForm(f => ({ ...f, description: e.target.value }))} />
              </LightField>
              <LightField label="Type">
                <select style={inputLight} value={actionForm.action_type} onChange={e => setActionForm(f => ({ ...f, action_type: e.target.value }))}>
                  <option value="key">⌨️ Touche clavier</option>
                  <option value="combo">⌨️ Combinaison (ex: ctrl+c)</option>
                  <option value="gamepad">🎮 Bouton manette</option>
                </select>
              </LightField>
              <LightField label={actionForm.action_type === 'gamepad' ? 'Bouton (ex: A, LB, RT)' : actionForm.action_type === 'combo' ? 'Touches (ex: ctrl+c)' : 'Touche (ex: space, f, e)'}>
                <input style={inputLight} value={actionForm.action_value}
                  placeholder={actionForm.action_type === 'gamepad' ? 'A, B, X, Y, LB…' : actionForm.action_type === 'combo' ? 'ctrl+c' : 'space'}
                  onChange={e => setActionForm(f => ({ ...f, action_value: e.target.value }))} />
              </LightField>
              <LightField label="Durée d'appui (ms)">
                <input style={inputLight} type="number" min={50} max={5000} step={50} value={actionForm.duration_ms}
                  onChange={e => setActionForm(f => ({ ...f, duration_ms: parseInt(e.target.value) || 100 }))} />
              </LightField>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                <button style={btnGhost} onClick={() => setShowActionForm(false)}>Annuler</button>
                <button style={btnPrimary} onClick={saveAction}>{editingAction ? 'Enregistrer' : 'Ajouter'}</button>
              </div>
            </Modal>
          )}
        </div>
      );
    }
    // Sinon → liste simple
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {headerSlot && <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '10px 12px 0', flexShrink: 0 }}>{headerSlot}</div>}
        {/* Toolbar */}
        <div style={{ padding: '10px 12px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 8, flexShrink: 0 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <svg style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
            </svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…" style={{ ...inputLight, paddingLeft: 28, fontSize: 12 }} />
          </div>
          {isAdmin && <button onClick={openAddGame} style={btnPrimary}>+ Nouveau</button>}
        </div>

        {/* Simple list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filteredGames.length === 0 && (
            <div style={{ padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              {search ? 'Aucun résultat' : 'Aucun jeu'}
            </div>
          )}
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredGames.map(g => (
            <div key={g.id}
              onClick={() => { setSelected(g); loadActions(g.id); }}
              style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
                border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              {/* Thumbnail */}
              <div style={{ width: '100%', height: g.image_url ? 130 : 90, overflow: 'hidden', background: g.image_url ? '#f1f5f9' : '#eff6ff', flexShrink: 0 }}>
                {g.image_url ? (
                  <img src={g.image_url} alt={g.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#cbd5e1' }}>🎮</div>
                )}
              </div>
              {/* Info */}
              <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#0f172a', fontWeight: 700, fontSize: 13, lineHeight: 1.3, wordBreak: 'break-word' }}>{g.name}</div>
                <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>{(g as any).action_count ?? 0} action{((g as any).action_count ?? 0) !== 1 ? 's' : ''}</div>
              </div>
              {/* Chevron */}
              <span style={{ color: '#cbd5e1', fontSize: 14, flexShrink: 0 }}>›</span>
              {/* Admin edit/delete */}
              {isAdmin && (
                <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                  <button onClick={e => openEditGame(g, e)} style={iconBtn} title="Modifier">✏️</button>
                  <button onClick={e => handleDeleteGame(g, e)} style={{ ...iconBtn, color: '#dc2626' }} title="Supprimer">🗑</button>
                </div>
              )}
              </div>
            </div>
          ))}
          </div>
        </div>

        {/* Modals */}
        {showGameForm && (
          <Modal title={editingGame ? 'Modifier le jeu' : 'Ajouter un jeu'} onClose={() => setShowGameForm(false)}>
            <LightField label="Nom du jeu">
              <input style={inputLight} value={gameForm.name} placeholder="ex: Minecraft"
                onChange={e => { const name = e.target.value; setGameForm(f => ({ ...f, name, slug: editingGame ? f.slug : slugify(name) })); }} />
            </LightField>
            {!editingGame && (
              <LightField label="Identifiant unique (slug)">
                <input style={inputLight} value={gameForm.slug} placeholder="ex: minecraft"
                  onChange={e => setGameForm(f => ({ ...f, slug: e.target.value }))} />
              </LightField>
            )}
            <LightField label="URL image couverture (portrait 2:3)">
              <input style={inputLight} value={gameForm.image_url} placeholder="https://…"
                onChange={e => setGameForm(f => ({ ...f, image_url: e.target.value }))} />
            </LightField>
            {gameForm.image_url && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <img src={gameForm.image_url} alt="preview" style={{ width: 70, height: 105, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }}
                  onError={e => (e.target as HTMLImageElement).style.display = 'none'} />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button style={btnGhost} onClick={() => setShowGameForm(false)}>Annuler</button>
              <button style={btnPrimary} onClick={saveGame}>{editingGame ? 'Enregistrer' : 'Ajouter'}</button>
            </div>
          </Modal>
        )}
        {showActionForm && (
          <Modal title={editingAction ? "Modifier l'action" : 'Nouvelle action'} onClose={() => setShowActionForm(false)}>
            <LightField label="Nom de l'action">
              <input style={inputLight} value={actionForm.name} placeholder="ex: Sauter"
                onChange={e => setActionForm(f => ({ ...f, name: e.target.value }))} />
            </LightField>
            <LightField label="Description (optionnel)">
              <input style={inputLight} value={actionForm.description} placeholder="ex: Fait sauter le personnage"
                onChange={e => setActionForm(f => ({ ...f, description: e.target.value }))} />
            </LightField>
            <LightField label="Type">
              <select style={inputLight} value={actionForm.action_type} onChange={e => setActionForm(f => ({ ...f, action_type: e.target.value }))}>
                <option value="key">⌨️ Touche clavier</option>
                <option value="combo">⌨️ Combinaison (ex: ctrl+c)</option>
                <option value="gamepad">🎮 Bouton manette</option>
              </select>
            </LightField>
            <LightField label={actionForm.action_type === 'gamepad' ? 'Bouton (ex: A, LB, RT)' : actionForm.action_type === 'combo' ? 'Touches (ex: ctrl+c)' : 'Touche (ex: space, f, e)'}>
              <input style={inputLight} value={actionForm.action_value}
                placeholder={actionForm.action_type === 'gamepad' ? 'A, B, X, Y, LB…' : actionForm.action_type === 'combo' ? 'ctrl+c' : 'space'}
                onChange={e => setActionForm(f => ({ ...f, action_value: e.target.value }))} />
            </LightField>
            <LightField label="Durée d'appui (ms)">
              <input style={inputLight} type="number" min={50} max={5000} step={50} value={actionForm.duration_ms}
                onChange={e => setActionForm(f => ({ ...f, duration_ms: parseInt(e.target.value) || 100 }))} />
            </LightField>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button style={btnGhost} onClick={() => setShowActionForm(false)}>Annuler</button>
              <button style={btnPrimary} onClick={saveAction}>{editingAction ? 'Enregistrer' : 'Ajouter'}</button>
            </div>
          </Modal>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>

      {/* ── Liste jeux ────────────────────────────────── */}
      <div style={{ width: 320, flexShrink: 0, background: '#f8fafc', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {headerSlot && <div style={{ padding: '14px 16px 0', flexShrink: 0 }}>{headerSlot}</div>}
        {/* Toolbar */}
        <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ color: '#475569', fontSize: 13, fontWeight: 600, flex: 1 }}>
              {games.length} jeu{games.length !== 1 ? 'x' : ''}
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <svg style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
            </svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…" style={{ ...inputLight, paddingLeft: 28, fontSize: 12 }} />
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filteredGames.map(g => {
            const isActive = selected?.id === g.id;
            return (
              <div key={g.id} onClick={() => setSelected(g)}
                className="game-card"
                style={{ background: isActive ? '#eff6ff' : '#fff', borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
                  border: `1px solid ${isActive ? '#bfdbfe' : '#e2e8f0'}`, boxShadow: isActive ? '0 0 0 3px rgba(37,99,235,0.08)' : '0 1px 2px rgba(0,0,0,0.04)',
                  display: 'flex', flexDirection: 'column', flexShrink: 0, transition: 'all 0.12s' }}>
                {/* Thumbnail */}
                <div style={{ width: '100%', height: g.image_url ? 130 : 90, overflow: 'hidden', background: g.image_url ? '#f1f5f9' : '#eff6ff', flexShrink: 0 }}>
                  {g.image_url ? (
                    <img src={g.image_url} alt={g.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#cbd5e1' }}>🎮</div>
                  )}
                </div>
                {/* Info */}
                <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: isActive ? '#2563eb' : '#0f172a', fontWeight: 700, fontSize: 13, lineHeight: 1.3, wordBreak: 'break-word' }}>{g.name}</div>
                    <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>{(g as any).action_count ?? 0} action{((g as any).action_count ?? 0) !== 1 ? 's' : ''}</div>
                  </div>
                {/* Admin actions */}
                {isAdmin && (
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    <button onClick={e => openEditGame(g, e)} style={iconBtn} title="Modifier">
                      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                    <button onClick={e => handleDeleteGame(g, e)} style={{ ...iconBtn, color: '#dc2626' }} title="Supprimer">
                      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                )}
                </div>
              </div>
            );
          })}
          {filteredGames.length === 0 && (
            <div style={{ padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              {search ? 'Aucun résultat' : 'Aucun jeu'}
            </div>
          )}
        </div>
        {isAdmin && (
          <div style={{ padding: 12, borderTop: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
            <button onClick={openAddGame} style={{ ...btnPrimary, width: '100%', justifyContent: 'center' }}>+ Nouveau jeu</button>
          </div>
        )}
      </div>

      {/* ── Détail jeu ──────────────────────────────────── */}
      {selected ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
              {selected.image_url && (
                <img src={selected.image_url} alt={selected.name}
                  style={{ width: 44, height: 66, objectFit: 'cover', borderRadius: 7, flexShrink: 0, border: '1px solid #e2e8f0' }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ color: '#0f172a', fontWeight: 700, fontSize: 17 }}>{selected.name}</div>
                <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>{actions.length} action{actions.length !== 1 ? 's' : ''}</div>
              </div>
              {isAdmin && (
                <button onClick={openAddAction} style={btnPrimary}>+ Action</button>
              )}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', background: '#f8fafc' }}>
              {loadingActions ? (
                <div style={{ color: '#94a3b8', textAlign: 'center', paddingTop: 40 }}>Chargement…</div>
              ) : actions.length === 0 ? (
                <div style={{ textAlign: 'center', paddingTop: 60, color: '#94a3b8', fontSize: 13 }}>
                  Aucune action.{isAdmin ? ' Clique sur "+ Action".' : ''}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {actions.map(a => (
                    <div key={a.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0, border: '1px solid #dbeafe' }}>
                        {a.action_type === 'gamepad' ? '🎮' : '⌨️'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 14 }}>{a.name}</div>
                        {a.description && <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>{a.description}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                        <span style={keyBadge}>{formatKey(a.action_value)}</span>
                        <span style={{ color: '#94a3b8', fontSize: 11, whiteSpace: 'nowrap' }}>{a.duration_ms} ms</span>
                      </div>
                      {isAdmin && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => openEditAction(a)} style={iconBtn}>
                            <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button onClick={() => handleDeleteAction(a)} style={{ ...iconBtn, color: '#dc2626' }}>
                            <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 52 }}>Jeux</div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>S&eacute;lectionnez un jeu</div>
          <div style={{ fontSize: 13 }}>ou cr&eacute;ez-en un nouveau</div>
        </div>
      )}


      {/* ── Modal jeu ── */}
      {showGameForm && (
        <Modal title={editingGame ? 'Modifier le jeu' : 'Ajouter un jeu'} onClose={() => setShowGameForm(false)}>
          <LightField label="Nom du jeu">
            <input style={inputLight} value={gameForm.name} placeholder="ex: Minecraft"
              onChange={e => { const name = e.target.value; setGameForm(f => ({ ...f, name, slug: editingGame ? f.slug : slugify(name) })); }} />
          </LightField>
          {!editingGame && (
            <LightField label="Identifiant unique (slug)">
              <input style={inputLight} value={gameForm.slug} placeholder="ex: minecraft"
                onChange={e => setGameForm(f => ({ ...f, slug: e.target.value }))} />
            </LightField>
          )}
          <LightField label="URL image couverture (portrait 2:3)">
            <input style={inputLight} value={gameForm.image_url} placeholder="https://…"
              onChange={e => setGameForm(f => ({ ...f, image_url: e.target.value }))} />
          </LightField>
          {gameForm.image_url && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img src={gameForm.image_url} alt="preview" style={{ width: 70, height: 105, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }}
                onError={e => (e.target as HTMLImageElement).style.display = 'none'} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button style={btnGhost} onClick={() => setShowGameForm(false)}>Annuler</button>
            <button style={btnPrimary} onClick={saveGame}>{editingGame ? 'Enregistrer' : 'Ajouter'}</button>
          </div>
        </Modal>
      )}

      {/* ── Modal action ── */}
      {showActionForm && (
        <Modal title={editingAction ? "Modifier l'action" : 'Nouvelle action'} onClose={() => setShowActionForm(false)}>
          <LightField label="Nom de l'action">
            <input style={inputLight} value={actionForm.name} placeholder="ex: Sauter"
              onChange={e => setActionForm(f => ({ ...f, name: e.target.value }))} />
          </LightField>
          <LightField label="Description (optionnel)">
            <input style={inputLight} value={actionForm.description} placeholder="ex: Fait sauter le personnage"
              onChange={e => setActionForm(f => ({ ...f, description: e.target.value }))} />
          </LightField>
          <LightField label="Type">
            <select style={inputLight} value={actionForm.action_type} onChange={e => setActionForm(f => ({ ...f, action_type: e.target.value }))}>
              <option value="key">⌨️ Touche clavier</option>
              <option value="combo">⌨️ Combinaison (ex: ctrl+c)</option>
              <option value="gamepad">🎮 Bouton manette</option>
            </select>
          </LightField>
          <LightField label={actionForm.action_type === 'gamepad' ? 'Bouton (ex: A, LB, RT)' : actionForm.action_type === 'combo' ? 'Touches (ex: ctrl+c)' : 'Touche (ex: space, f, e)'}>
            <input style={inputLight} value={actionForm.action_value}
              placeholder={actionForm.action_type === 'gamepad' ? 'A, B, X, Y, LB…' : actionForm.action_type === 'combo' ? 'ctrl+c' : 'space'}
              onChange={e => setActionForm(f => ({ ...f, action_value: e.target.value }))} />
          </LightField>
          <LightField label="Durée d'appui (ms)">
            <input style={inputLight} type="number" min={50} max={5000} step={50} value={actionForm.duration_ms}
              onChange={e => setActionForm(f => ({ ...f, duration_ms: parseInt(e.target.value) || 100 }))} />
          </LightField>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button style={btnGhost} onClick={() => setShowActionForm(false)}>Annuler</button>
            <button style={btnPrimary} onClick={saveAction}>{editingAction ? 'Enregistrer' : 'Ajouter'}</button>
          </div>
        </Modal>
      )}

      <style>{`
        .game-row:hover .row-actions { opacity: 1 !important; }
        .game-row:hover { background: #f8fafc !important; }
      `}</style>
    </div>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 24, width: 440, maxWidth: '92vw', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ color: '#0f172a', fontWeight: 700, fontSize: 16 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function LightField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ color: '#374151', fontSize: 13, fontWeight: 500 }}>{label}</div>
      {children}
    </div>
  );
}

const inputLight: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#0f172a', fontSize: 13, boxSizing: 'border-box', outline: 'none' };
const btnPrimary: React.CSSProperties = { padding: '7px 14px', borderRadius: 7, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' };
const btnGhost: React.CSSProperties = { padding: '8px 16px', borderRadius: 7, border: '1px solid #e2e8f0', background: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', padding: '4px 7px', fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center' };
const keyBadge: React.CSSProperties = { background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 4, padding: '2px 8px', fontSize: 11, color: '#2563eb', fontFamily: 'monospace', whiteSpace: 'nowrap' };
